import React, { useCallback, useRef, useState } from "react";
import {
    Facebook as FacebookIcon,
    LinkedIn as LinkedInIcon,
    Link as LinkIcon,
    Share as ShareIcon,
    WhatsApp as WhatsAppIcon
} from "@mui/icons-material";
import {
    Box,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    SvgIcon,
    Tooltip
} from "@mui/material";
import Logger from "../../../../services/Logger";
import { playPillButtonSx } from "../../../../dark-theme/surfaces";

// Brand icons for platforms not in MUI
const RedditIcon = () => (
    <SvgIcon sx={{ fontSize: 24 }} viewBox="0 0 24 24"><path fill="currentColor" d="M14.5 15c0 1.11-.89 2-2 2s-2-.89-2-2 .89-2 2-2 2 .89 2 2M21 12.5c0-1.58-1.39-2.86-3.08-2.99.22-1.24.17-2.55-.14-3.73-.79-3.02-3.08-4.97-6.29-5.27L12.5 0h-1.34l-.5 5.51C7.39 5.84 5.09 7.79 4.32 10.78c-.31 1.18-.36 2.49-.14 3.73C2.39 9.64 1 10.92 1 12.5c0 1.52 1.17 2.77 2.67 3l-.17 1.12c-.13.95.07 1.93.55 2.71.59.97 1.56 1.58 2.68 1.58h9.78c1.12 0 2.09-.61 2.67-1.58.48-.78.68-1.76.55-2.71l-.17-1.12C19.83 15.27 21 14.02 21 12.5M12 22a3.5 3.5 0 0 1-3.5-3.5 3.5 3.5 0 0 1 7 0 3.5 3.5 0 0 1-3.5 3.5m-1.67-8.5c1.06 0 1.92-.86 1.92-1.92s-.86-1.92-1.92-1.92-1.92.86-1.92 1.92.86 1.92 1.92 1.92z"/></SvgIcon>
);
const TelegramIcon = () => (
    <SvgIcon sx={{ fontSize: 24 }} viewBox="0 0 24 24"><path fill="currentColor" d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.611-.06-2.515-.246-.442-.091-.938-.196-1.379-.246a2.77 2.77 0 0 1-.248-.098c-.394-.17-.669-.263-.635-.39.033-.127.42-.253.89-.39.344-.106.814-.247 1.253-.346.435-.098.816-.148.93-.164.224-.037.376.025.422.067.046.042.05.138.004.216-.21.346-.63.804-.861 1.078-.306.36-.527.622-.573.68-.046.057-.095.132-.024.207.07.075.305.322.436.434.19.16.328.274.395.295.103.033.203-.032.314-.209.11-.177.19-.38.24-.573.06-.203.02-.378-.02-.454-.039-.076-.35-.298-.5-.406-.151-.108-.258-.178-.258-.178s.026-.058.07-.084c.043-.024.1-.039.1-.039z"/></SvgIcon>
);
const LineIcon = () => (
    <SvgIcon sx={{ fontSize: 24 }} viewBox="0 0 24 24"><path fill="currentColor" d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.349 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.242 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/></SvgIcon>
);

const PREVIEW_BASE = process.env.REACT_APP_PREVIEW_IMAGE_BASE || 'https://puzzleinteract-images.s3.us-east-1.amazonaws.com/preview';

/** Mobile detection for app deep linking */
const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');
const isAndroid = () => /Android/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');

/**
 * Build Android Intent URL - opens native app if installed, otherwise falls back to web.
 * @param {string} webUrl - Full web URL for sharing
 * @param {string} packageName - Android package (e.g. com.facebook.katana)
 * @returns {string} Intent URL or webUrl if not Android
 */
const buildAndroidIntentUrl = (webUrl, packageName) => {
    if (!isAndroid()) return webUrl;
    const encodedFallback = encodeURIComponent(webUrl);
    // Intent format: intent://path#Intent;scheme=https;package=X;S.browser_fallback_url=Y;end
    const match = webUrl.match(/^https?:\/\/([^/]+)(\/.*)?$/);
    const path = match ? `${match[1]}${match[2] || '/'}` : webUrl.replace(/^https?:\/\//, '');
    return `intent://${path}#Intent;scheme=https;package=${packageName};S.browser_fallback_url=${encodedFallback};end`;
};

/**
 * Build preview image URL for puzzles. S3 path: {base}/{type}/{puzzleId}.png
 */
export const buildPreviewImageUrl = (puzzleId, puzzleType) => {
    if (!puzzleId) return '';
    const typeUpper = (puzzleType || '').toUpperCase();
    let folder = 'word_search';
    if (typeUpper === 'CROSSWORD') folder = 'crossword';
    else if (typeUpper === 'HIDDEN_MESSAGE') folder = 'hidden_message';
    return `${PREVIEW_BASE}/${folder}/${puzzleId}.png`;
};

const PUBLIC_SHARE_BASE = process.env.REACT_APP_PUBLIC_URL || 'https://www.puzzleinteract.com';

/**
 * Base URL for shared links. Must be publicly crawlable for Facebook, LinkedIn, etc. to show preview (image, title, description).
 * - On localhost: use production URL so crawlers can reach the share page and read og:meta.
 * - On non-http(s) origins: use production URL so shared links open in a browser.
 * - On production https: use current origin.
 */
const getShareBaseUrl = () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (origin.startsWith('http') && !/localhost|127\.0\.0\.1/i.test(origin)) return origin;
    return (PUBLIC_SHARE_BASE || '').trim().replace(/\/$/, '') || 'https://www.puzzleinteract.com';
};

/**
 * Build the share URL for social previews (Facebook, etc.). Must be used when sharing – not the play URL.
 * Format: {base}/share/{PUZZLE_TYPE}/{puzzleId} or …/{puzzleId}/story for story context
 * e.g. https://www.puzzleinteract.com/share/CROSSWORD/{id}
 * @param {string} puzzleId - Template ID. For crosswords: crossTplId; for grid: gridTplId.
 * @param {string} puzzleType - CROSSWORD, WORD_SEARCH, HIDDEN_MESSAGE, SLIDING, JIGSAW, …
 * @param {boolean} [isStory] - When true, append /story (share landing loads puzzle with story API context).
 * @returns {string} Full share URL (not play URL), e.g. https://www.puzzleinteract.com/share/JIGSAW/{id}
 */
export const buildShareUrl = (puzzleId, puzzleType, isStory = false) => {
    const id = typeof puzzleId === "string" ? puzzleId.trim() : String(puzzleId ?? "").trim();
    if (!id) return "";
    const base = getShareBaseUrl();
    const typeUpper = (puzzleType || "WORD_SEARCH").toUpperCase();
    let url = `${base.replace(/\/$/, "")}/share/${typeUpper}/${encodeURIComponent(id)}`;
    if (isStory) url += "/story";
    return url;
};

/**
 * Crawler-friendly story share URL: `/share/{storyId}/story` on the public site (same base as puzzle shares).
 */
export function buildStorySharePageUrl(storyId) {
    const id = typeof storyId === "string" ? storyId.trim() : String(storyId ?? "").trim();
    if (!id) return "";
    const path = `/share/${encodeURIComponent(id)}/story`;
    const shareBase = getShareBaseUrl().replace(/\/$/, "");
    if (shareBase) return `${shareBase}${path}`;
    if (typeof window !== "undefined" && window.location?.origin) {
        return `${window.location.origin.replace(/\/$/, "")}${path}`;
    }
    return path;
}

/** True if URL looks like a play URL; those must not be used for sharing (no og:meta for crawlers). */
const isPlayUrl = (url) =>
    typeof url === 'string' &&
    /\/puzzle\/play-(cross|grid|hidden-message|word-search)(\/|$)/.test(url);

/** True when Web Share API is available (e.g. iOS Safari). Use so the system share sheet gets the share URL. */
const canNativeShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function';

/**
 * @param {string} puzzleTitle - Puzzle title for share text
 * @param {string} puzzleId - Template ID. For crosswords: crossTplId; for grid: gridTplId.
 * @param {string} puzzleType - CROSSWORD, WORD_SEARCH, or HIDDEN_MESSAGE
 * @param {string} [shareUrl] - Optional override; must be a share URL (/share/...), not a play URL. If play URL is passed, it is ignored.
 * @param {boolean} [openMenuAbove] - When true, menu opens above the button (e.g. when button is at bottom of page)
 * @param {boolean} [isStory] - Share link uses /share/{type}/{id}/story so landing fetches with story context.
 * @param {string} [shareMessage] - Full share body text (optional); overrides default puzzle wording.
 * @param {string} [tooltipTitle] - Tooltip on the share icon (optional).
 * @param {boolean} [compact] - Smaller icon button (e.g. header adornment).
 * @param {boolean} [compact] - Smaller icon button (e.g. header adornment).
 */
const SocialShareDropdown = ({
    puzzleTitle,
    puzzleId,
    puzzleType,
    onCopyLink,
    isDemo = false,
    shareUrl: shareUrlProp,
    openMenuAbove = false,
    isStory = false,
    shareMessage,
    tooltipTitle,
    compact = false,
}) => {
    const shareButtonRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const builtShareUrl = buildShareUrl(puzzleId, puzzleType, isStory);
    const shareUrl = (shareUrlProp && !isPlayUrl(shareUrlProp)) ? shareUrlProp : builtShareUrl;
    const shareText =
        typeof shareMessage === "string" && shareMessage.trim()
            ? shareMessage.trim()
            : `Check out this amazing ${(puzzleType || "puzzle").toLowerCase().replace("_", " ")} puzzle: "${puzzleTitle}"`;

    // Use Facebook sharer URL so no redirect_uri is needed. The Feed Dialog (dialog/feed) requires
    // redirect_uri to be whitelisted in the Facebook App; in Capacitor/WebView (e.g. iOS) the origin
    // is often capacitor://localhost or similar, which Facebook rejects with "redirect_url is not supported".
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

    const socialMediaActions = [
        ...((canNativeShare() && isMobile()) ? [{
            name: "Share",
            icon: <ShareIcon />,
            color: "#3BA666",
            action: "nativeShare",
            nativeShareUrl: shareUrl,
            nativeShareTitle: puzzleTitle || "Puzzle",
            nativeShareText: shareText
        }] : []),
        ...(!isMobile() ? [{
            name: "Facebook",
            icon: <FacebookIcon />,
            color: "#1877F2",
            url: facebookUrl,
            androidPackage: "com.facebook.katana"
        }] : []),
        {
            name: "X",
            icon: (
                <SvgIcon sx={{ fontSize: 24 }} viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor" />
                </SvgIcon>
            ),
            color: "#000000",
            url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
            androidPackage: "com.twitter.android"
        },
        {
            name: "LinkedIn",
            icon: <LinkedInIcon />,
            color: "#0A66C2",
            url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
            androidPackage: "com.linkedin.android"
        },
        {
            name: "WhatsApp",
            icon: <WhatsAppIcon />,
            color: "#25D366",
            url: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
            androidPackage: "com.whatsapp"
        },
        {
            name: "Reddit",
            icon: <RedditIcon />,
            color: "#FF4500",
            url: `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(puzzleTitle || shareText)}`,
            androidPackage: "com.reddit.frontpage"
        },
        {
            name: "Telegram",
            icon: <TelegramIcon />,
            color: "#0088CC",
            url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
            androidPackage: "org.telegram.messenger"
        },
        {
            name: "LINE",
            icon: <LineIcon />,
            color: "#00B900",
            url: `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}`,
            androidPackage: "jp.naver.line.android"
        },
        {
            name: "Copy Link",
            icon: <LinkIcon />,
            color: "#666",
            action: "copy"
        }
    ];

    const handleClick = (event) => {
        event.stopPropagation();
        if (open) {
            handleClose();
            return;
        }
        setAnchorEl(shareButtonRef.current);
    };

    const handleActionClick = async (action) => {
        handleClose();
        try {
            // In demo mode, don't perform actual actions
            if (isDemo) {
                return;
            }

            if (action.action === "nativeShare") {
                try {
                    await navigator.share({
                        title: action.nativeShareTitle || puzzleTitle || "Puzzle",
                        text: action.nativeShareText || shareText,
                        url: action.nativeShareUrl || shareUrl
                    });
                    if (onCopyLink) onCopyLink();
                } catch (err) {
                    if (err.name !== 'AbortError') Logger.error('Native share failed:', err);
                }
            } else if (action.action === "copy") {
                try {
                    await navigator.clipboard.writeText(shareUrl);
                    if (onCopyLink) onCopyLink();
                } catch (err) {
                    Logger.error('Failed to copy link:', err);
                }
            } else if (action.url) {
                const urlToOpen = (isMobile() && action.androidPackage)
                    ? buildAndroidIntentUrl(action.url, action.androidPackage)
                    : action.url;
                window.open(urlToOpen, '_blank');
            }
        } catch (error) {
            Logger.error('Error in handleActionClick:', error);
        }
    };

    return (
        <Box
            component="span"
            data-play-toolbar-no-drag
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{ display: "inline-flex", flexShrink: 0 }}
        >
            <Tooltip title={tooltipTitle || "Share puzzle"} arrow>
                <IconButton
                    ref={shareButtonRef}
                    size={compact ? "small" : "medium"}
                    onClick={handleClick}
                    sx={{
                        borderRadius: "50%",
                        ...(compact
                            ? {
                                  width: 28,
                                  height: 28,
                                  padding: "5px",
                                  color: "#3BA666",
                                  backgroundColor: "transparent",
                                  boxShadow: "none",
                                  "&:hover": {
                                      backgroundColor: "action.hover",
                                  },
                                  "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
                              }
                            : playPillButtonSx),
                    }}
                >
                    <ShareIcon />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                disableScrollLock
                slotProps={{
                    list: {
                        onClick: (e) => e.stopPropagation(),
                    },
                }}
                anchorOrigin={{
                    vertical: openMenuAbove ? 'top' : 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: openMenuAbove ? 'bottom' : 'top',
                    horizontal: 'left',
                }}
            >
                {socialMediaActions.map((action) => (
                    <MenuItem
                        key={action.name}
                        onClick={() => handleActionClick(action)}
                        sx={{
                            '&:hover': {
                                backgroundColor: action.color,
                                color: '#fff',
                                '& .MuiListItemIcon-root': {
                                    color: '#fff',
                                },
                                '& .MuiListItemText-primary': {
                                    color: '#fff',
                                },
                            },
                        }}
                    >
                        <ListItemIcon sx={{ color: action.color }}>
                            {action.icon}
                        </ListItemIcon>
                        <ListItemText primary={action.name} />
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default SocialShareDropdown;

